import { useEffect, useState } from "react";
import { Clock3 } from "lucide-react";
import { apiGet } from "@/lib/apiClient";

type TrialPlanResponse = {
  isTrial?: boolean;
  trialDaysRemaining?: number | null;
  trialEndsAt?: string | null;
  trialExpired?: boolean;
};

const DAY_MS = 24 * 60 * 60 * 1000;

export function TrialStatusBadge() {
  const [data, setData] = useState<TrialPlanResponse | null>(null);

  useEffect(() => {
    let active = true;
    void (async () => {
      const res = await apiGet<TrialPlanResponse>("/api/billing/me/plan");
      if (active && res.ok) setData(res.data ?? null);
    })();
    return () => {
      active = false;
    };
  }, []);

  if (!data?.isTrial) return null;

  const trialExpired = data.trialExpired === true;
  const daysRemaining = typeof data.trialDaysRemaining === "number" ? data.trialDaysRemaining : null;
  const endsAtMs = Date.parse(String(data.trialEndsAt || ""));
  const endsWithinDay = Number.isFinite(endsAtMs) && endsAtMs - Date.now() <= DAY_MS;
  const showEndsToday = !trialExpired && (daysRemaining === 0 || endsWithinDay);

  const label = trialExpired
    ? "Free trial ended"
    : showEndsToday
      ? "Trial ends today"
      : typeof daysRemaining === "number"
        ? `${daysRemaining} day${daysRemaining === 1 ? "" : "s"} left in free trial`
        : "Free trial in progress";

  const toneClass = trialExpired
    ? "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/30 dark:bg-rose-500/10 dark:text-rose-200"
    : "border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100";

  return (
    <div
      className={`hidden items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold md:inline-flex ${toneClass}`}
      aria-label={label}
      title={label}
    >
      <Clock3 className="size-3.5 shrink-0" aria-hidden />
      <span>{label}</span>
    </div>
  );
}
